import { useMemo } from 'react';

import type { Principal, PrincipalAndRole, Role } from '../../../commonTypes.tsx';
import type { PrincipalsMap } from '../../../../controller/principalsMap.ts';
import { shouldMutationIncludeInvites } from '../../../../controller/mutations/restrictionsTransformer.ts';
import type { SpecificAccessDetailsProps } from '../specificAccessDetails/SpecificAccessDetails.tsx';

import {
	applySpecificAccessLiveDelta,
	createPrincipalsMapFromSpecificAccessRows,
	getNonNullSpecificAccessPrincipals,
	type SpecificAccessLiveDelta,
	shouldIncludeInvitesForSpecificAccessLiveMutation,
} from './specificAccessDialogLiveState.ts';

export function useLiveSpecificAccessDerivedState({
	specificAccessDetails,
	currentUserId,
	liveSpecificAccessDelta,
	principalsPendingAccess,
}: {
	specificAccessDetails?: SpecificAccessDetailsProps;
	currentUserId: string;
	liveSpecificAccessDelta: SpecificAccessLiveDelta;
	principalsPendingAccess: PrincipalAndRole[];
}): {
	basePrincipalsMap: PrincipalsMap;
	effectivePrincipalsMap: PrincipalsMap;
	includeInvites: boolean;
	specificAccessPrincipalById: Map<string, { principal: Principal; role: Role }>;
} {
	const basePrincipalsMap = useMemo((): PrincipalsMap => {
		const principals = getNonNullSpecificAccessPrincipals(specificAccessDetails);

		return createPrincipalsMapFromSpecificAccessRows({
			principals,
		});
	}, [specificAccessDetails]);

	const effectivePrincipalsMap = useMemo((): PrincipalsMap => {
		return applySpecificAccessLiveDelta({
			basePrincipalsMap,
			currentUserId,
			liveDelta: liveSpecificAccessDelta,
		});
	}, [basePrincipalsMap, currentUserId, liveSpecificAccessDelta]);

	const includeInvites = useMemo(() => {
		return shouldIncludeInvitesForSpecificAccessLiveMutation({
			principals: effectivePrincipalsMap,
			liveDelta: liveSpecificAccessDelta,
			principalsPendingAccess,
			shouldMutationIncludeInvites,
		});
	}, [effectivePrincipalsMap, liveSpecificAccessDelta, principalsPendingAccess]);

	const specificAccessPrincipalById = useMemo(() => {
		const map = new Map<string, { principal: Principal; role: Role }>();
		for (const row of specificAccessDetails?.principals ?? []) {
			if (!row) {
				continue;
			}
			map.set(row.principalDetails.principal.id, {
				principal: row.principalDetails.principal,
				role: row.principalDetails.role,
			});
		}
		return map;
	}, [specificAccessDetails]);

	return { basePrincipalsMap, effectivePrincipalsMap, includeInvites, specificAccessPrincipalById };
}
